import { format, isValid, parseISO, startOfDay, subDays } from 'date-fns'

import type { AppointmentRecord } from '@/features/appointments/types'
import { parseAppointmentDate } from '@/features/appointments/utils/parseAppointmentDate'
import type { CallRecord } from '@/features/calls/types'
import type { ClientDuty } from '@/features/patients/hooks/useClientDutiesQuery'
import type { VisitRecord } from '@/features/visits/types'
import { appointmentDate } from './clientHistory'

export type TimelineKind = 'call' | 'duty' | 'visit' | 'appointment'

export type TimelineItem =
  | { kind: 'call'; id: string; date: Date; call: CallRecord }
  | { kind: 'duty'; id: string; date: Date; duty: ClientDuty }
  | { kind: 'visit'; id: string; date: Date; visit: VisitRecord }
  | {
      kind: 'appointment'
      id: string
      date: Date
      appointment: AppointmentRecord
    }

export interface TimelineSources {
  calls: CallRecord[]
  /** More calls exist than are loaded; older entries would be missing calls. */
  callsTruncated: boolean
  duties: ClientDuty[]
  visits: VisitRecord[]
  appointments: AppointmentRecord[]
}

function instant(value: string | null): Date | null {
  if (!value) return null
  const date = parseISO(value)
  return isValid(date) ? date : null
}

/** Every dated event across the sources; undated rows have no place on it. */
function collect(sources: TimelineSources): TimelineItem[] {
  const items: (TimelineItem | null)[] = [
    ...sources.calls.map((call) => {
      const date = instant(call.created_at)
      return date
        ? { kind: 'call' as const, id: call.call_id, date, call }
        : null
    }),
    ...sources.duties.map((duty) => {
      const date = instant(duty.assigned_at)
      return date
        ? { kind: 'duty' as const, id: duty.patient_duty_id, date, duty }
        : null
    }),
    ...sources.visits.map((visit) => {
      const date = parseAppointmentDate(visit.visit_date)
      return date ? { kind: 'visit' as const, id: visit.id, date, visit } : null
    }),
    ...sources.appointments.map((appointment) => {
      const date = appointmentDate(appointment)
      return date
        ? {
            kind: 'appointment' as const,
            id: appointment.id,
            date,
            appointment,
          }
        : null
    }),
  ]
  return items.filter((item): item is TimelineItem => item !== null)
}

/**
 * The client's history as one feed, limited to `kinds`.
 *
 * Upcoming appointments come first, soonest at the top; everything else
 * follows newest first. While older calls are still unloaded, the feed stops
 * at the oldest loaded call so it never shows a stretch of history with its
 * calls missing.
 */
export function buildTimeline(
  sources: TimelineSources,
  kinds: ReadonlySet<TimelineKind>,
  now: Date
): TimelineItem[] {
  const today = startOfDay(now).getTime()
  let items = collect(sources).filter((item) => kinds.has(item.kind))

  if (sources.callsTruncated && kinds.has('call')) {
    const oldestCall = Math.min(
      ...items.filter((i) => i.kind === 'call').map((i) => i.date.getTime())
    )
    items = items.filter((item) => item.date.getTime() >= oldestCall)
  }

  const upcoming: TimelineItem[] = []
  const past: TimelineItem[] = []
  for (const item of items) {
    const isUpcoming =
      item.kind === 'appointment' && item.date.getTime() >= today
    ;(isUpcoming ? upcoming : past).push(item)
  }
  return [
    ...upcoming.toSorted((a, b) => a.date.getTime() - b.date.getTime()),
    ...past.toSorted((a, b) => b.date.getTime() - a.date.getTime()),
  ]
}

export interface TimelineGroup {
  label: string
  items: TimelineItem[]
}

/** "Upcoming", "Last 7 days", then one group per month ("August 2026"). */
export function groupTimeline(
  items: TimelineItem[],
  now: Date
): TimelineGroup[] {
  const today = startOfDay(now).getTime()
  const lastWeekStart = subDays(startOfDay(now), 6).getTime()
  const groups: TimelineGroup[] = []

  for (const item of items) {
    const time = item.date.getTime()
    const label =
      item.kind === 'appointment' && time >= today
        ? 'Upcoming'
        : time >= lastWeekStart
          ? 'Last 7 days'
          : format(item.date, 'MMMM yyyy')
    const last = groups.at(-1)
    if (last?.label === label) last.items.push(item)
    else groups.push({ label, items: [item] })
  }
  return groups
}
